import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  startOfWeek,
  endOfWeek,
  addMinutes,
  addHours,
  addDays,
  startOfDay,
  endOfDay,
  getDay,
  isSameDay
} from 'date-fns';
import { CalendarApiService } from '../../services/calendar-api.service';
import { AuthService } from '../../services/auth.service';
import { CreateAppointmentRequest } from '../../api/models/create-appointment-request';
import { Appointment } from 'src/app/api/models/appointment';
import { environment } from 'src/environments/environment';
import { Api } from '../../api/api';
import { getEmployeeLocation$Json } from '../../api/fn/employee-location/get-employee-location-json';
import { EmployeeLocationModel } from '../../api/models/employee-location-model';

interface LinkParameters {
  user1Id: string;
  user2Id: string;
  validationKey: string;
}

/** A single selectable appointment time range within a day. */
interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
  /**
   * True when the slot is within business hours and does not overlap an
   * appointment directly, but falls inside the buffer window around one
   * (so it is not bookable). Used to style it distinctly from fully
   * unavailable slots.
   */
  bufferBlocked: boolean;
}

/** A weekday with its location and the list of bookable time ranges. */
interface DaySchedule {
  date: Date;
  location: string | null;
  slots: TimeSlot[];
  hasAvailable: boolean;
  // Direction B: slots split into AM / PM groups for the guided layout.
  morning: TimeSlot[];
  afternoon: TimeSlot[];
}

/** A busy event used for overlap checks. */
interface BusyEvent {
  start: Date;
  end: Date;
}

@Component({
    selector: 'app-calendar-view',
    templateUrl: './calendar-view.component.html',
    styleUrls: ['./calendar-view.component.scss'],
    standalone: false
})
export class CalendarViewComponent implements OnInit {
  // Business hours (24h clock)
  private static readonly DAY_START_HOUR = 8;
  private static readonly DAY_END_HOUR = 17; // 5pm
  private static readonly SLOT_STEP_MINUTES = 15;
  // Time (minutes) that must be kept clear before and after every appointment.
  private static readonly APPOINTMENT_BUFFER_MINUTES = 15;

  viewDate: Date = new Date();
  events: BusyEvent[] = [];

  // Day list rendered in the view
  days: DaySchedule[] = [];

  // Direction B: index of the day currently shown in the day strip.
  selectedDayIndex: number = 0;

  linkParams!: LinkParameters;
  user1Email: string = '';
  user2Email: string = '';

  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // Appointment booking
  showAppointmentDialog: boolean = false;
  selectedSlotStart: Date | null = null;
  selectedSlotEnd: Date | null = null;
  appointmentSubject: string = '';
  appointmentNotes: string = '';

  // Meeting type info from JWT claims
  meetingMinutes: number = 60;
  meetingTypeName: string = 'Meeting';
  meetingLocationName: string = 'Location';
  caseId: number = 0;
  earliestSchedulableDate: Date = new Date();

  // Track initial load to auto-advance to next week if needed
  private isInitialLoad: boolean = true;

  // Employee location data
  employeeLocation: EmployeeLocationModel | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private calendarApiService: CalendarApiService,
    private authService: AuthService,
    private api: Api,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Load meeting type info from JWT claims
    this.meetingMinutes = this.authService.getMeetingMinutes();
    this.meetingTypeName = this.authService.getMeetingTypeName();
    this.meetingLocationName = this.authService.getMeetingLocationName();
    this.caseId = this.authService.getCaseId();

    // Calculate earliest schedulable date: buffer hours from the current time.
    const bufferHours = environment.appointmentBufferHours;
    this.earliestSchedulableDate = addHours(new Date(), bufferHours);

    // Get link parameters from query params
    this.route.queryParams.subscribe(params => {
      this.linkParams = {
        user1Id: params['user1Id'] || '',
        user2Id: params['user2Id'] || '',
        validationKey: params['key'] || ''
      };

      this.user1Email = this.authService.getUser1Email();
      this.user2Email = this.authService.getUser2Email();

      // Load employee location only for in-person meetings
      if (this.isInPersonMeeting()) {
        this.loadEmployeeLocation();
      }

      // Load calendar events
      this.loadCalendarEvents();
    });
  }

  /** The day currently selected in the day strip (Direction B). */
  get selectedDay(): DaySchedule | null {
    return this.days[this.selectedDayIndex] ?? null;
  }

  /** Select a day in the day strip (ignored if the day has no availability). */
  selectDay(index: number): void {
    if (this.days[index]?.hasAvailable) {
      this.selectedDayIndex = index;
    }
  }

  /** Whether the given slot is the one the user has currently picked. */
  isSelectedSlot(slot: TimeSlot): boolean {
    return (
      !!this.selectedSlotStart &&
      slot.start.getTime() === this.selectedSlotStart.getTime()
    );
  }

  /** Opens the confirm sheet for the currently picked slot. */
  reviewSelection(): void {
    if (this.selectedSlotStart && this.selectedSlotEnd) {
      this.showAppointmentDialog = true;
    }
  }

  /**
   * Checks if the current meeting location is in-person.
   */
  isInPersonMeeting(): boolean {
    return this.meetingLocationName?.toLowerCase() === 'in-person';
  }

  async loadEmployeeLocation(): Promise<void> {
    try {
      this.employeeLocation = await this.api.invoke(getEmployeeLocation$Json);
      this.buildDaySchedules();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading employee location:', error);
    }
  }

  loadCalendarEvents(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const start = this.getViewStartDate();
    const end = this.getViewEndDate();

    this.calendarApiService.getCalendarEvents(start, end)
      .subscribe({
        next: (events: Appointment[]) => {
          this.events = events.map(event => ({
            start: new Date(event.startDateTime!),
            end: new Date(event.endDateTime!)
          }));

          // On initial load, check if we should auto-advance to next week
          if (this.isInitialLoad && this.shouldAdvanceToNextWeek()) {
            this.isInitialLoad = false;
            this.viewDate = addDays(this.viewDate, 7);
            this.loadCalendarEvents();
            return;
          }
          this.isInitialLoad = false;

          this.buildDaySchedules();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading calendar events:', error);
          this.errorMessage = 'Failed to load calendar events. Please try again.';
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  getViewStartDate(): Date {
    const weekStart = startOfWeek(this.viewDate, { weekStartsOn: 0 });
    return startOfDay(weekStart);
  }

  getViewEndDate(): Date {
    const weekEnd = endOfWeek(this.viewDate, { weekStartsOn: 0 });
    return endOfDay(weekEnd);
  }

  /** Monday of the week currently being viewed. */
  get weekStart(): Date {
    return startOfWeek(this.viewDate, { weekStartsOn: 1 });
  }

  /** Friday of the week currently being viewed. */
  get weekEnd(): Date {
    return addDays(this.weekStart, 4);
  }

  /**
   * Builds the list of weekdays (Mon-Fri) for the current week, each with its
   * location and a set of selectable time ranges of the meeting duration.
   */
  private buildDaySchedules(): void {
    const days: DaySchedule[] = [];
    const monday = this.weekStart;

    for (let i = 0; i < 5; i++) {
      const date = addDays(monday, i);
      const slots = this.buildSlotsForDay(date);
      days.push({
        date,
        location: this.getLocationForDay(date),
        slots,
        hasAvailable: slots.some(s => s.available),
        morning: slots.filter(s => s.start.getHours() < 12),
        afternoon: slots.filter(s => s.start.getHours() >= 12)
      });
    }

    this.days = days;

    // Direction B: default the day strip to the first day that has openings.
    const firstAvailable = days.findIndex(d => d.hasAvailable);
    this.selectedDayIndex = firstAvailable >= 0 ? firstAvailable : 0;
  }

  /**
   * Builds every candidate time range for a day, stepping by SLOT_STEP_MINUTES
   * from the start of business hours until the latest start that still ends
   * within business hours.
   */
  private buildSlotsForDay(day: Date): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const latestStartMinutes =
      CalendarViewComponent.DAY_END_HOUR * 60 - this.meetingMinutes;

    for (
      let minutes = CalendarViewComponent.DAY_START_HOUR * 60;
      minutes <= latestStartMinutes;
      minutes += CalendarViewComponent.SLOT_STEP_MINUTES
    ) {
      const start = new Date(day);
      start.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
      const end = addMinutes(start, this.meetingMinutes);

      const inWindow = this.isWithinBookingWindow(start, end);
      const overlapsAppointment = this.overlapsEvent(start, end, 0);
      const overlapsBuffer = this.overlapsEvent(
        start,
        end,
        CalendarViewComponent.APPOINTMENT_BUFFER_MINUTES
      );

      slots.push({
        start,
        end,
        available: inWindow && !overlapsBuffer,
        bufferBlocked: inWindow && !overlapsAppointment && overlapsBuffer
      });
    }

    return slots;
  }

  previousPeriod(): void {
    this.viewDate = addDays(this.viewDate, -7);
    this.loadCalendarEvents();
  }

  nextPeriod(): void {
    this.viewDate = addDays(this.viewDate, 7);
    this.loadCalendarEvents();
  }

  today(): void {
    this.viewDate = new Date();
    this.loadCalendarEvents();
  }

  /**
   * Called when a contact taps a bookable time range. In Direction B this only
   * sets the pending selection (highlighting the chip + showing the sticky
   * "Selected" bar); the confirm sheet opens from reviewSelection().
   */
  selectSlot(slot: TimeSlot): void {
    if (!slot.available) {
      return;
    }

    this.selectedSlotStart = slot.start;
    this.selectedSlotEnd = slot.end;
    this.appointmentSubject = `${this.meetingTypeName} with ${this.user2Email}`;
    this.appointmentNotes = '';
    this.errorMessage = '';
    this.successMessage = '';
  }

  isTimeSlotAvailable(start: Date, end: Date): boolean {
    return (
      this.isWithinBookingWindow(start, end) &&
      !this.overlapsEvent(start, end, CalendarViewComponent.APPOINTMENT_BUFFER_MINUTES)
    );
  }

  /**
   * Checks whether a time range falls inside the bookable window: not in the
   * past (now + buffer hours), not on a weekend, and entirely within business
   * hours. Does not consider existing appointments.
   */
  private isWithinBookingWindow(start: Date, end: Date): boolean {
    if (start < this.earliestSchedulableDate) {
      return false;
    }

    const dayOfWeek = start.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false;
    }

    const startHour = start.getHours();
    if (startHour < CalendarViewComponent.DAY_START_HOUR) {
      return false;
    }

    const endHour = end.getHours();
    const endMinutes = end.getMinutes();
    if (
      endHour > CalendarViewComponent.DAY_END_HOUR ||
      (endHour === CalendarViewComponent.DAY_END_HOUR && endMinutes > 0)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Checks whether a time range overlaps any existing event, optionally padding
   * each event by `bufferMinutes` on both sides to enforce a gap between
   * appointments.
   */
  private overlapsEvent(start: Date, end: Date, bufferMinutes: number): boolean {
    return this.events.some(event => {
      const eventStart = addMinutes(event.start, -bufferMinutes);
      const eventEnd = addMinutes(event.end || event.start, bufferMinutes);
      return (start < eventEnd && end > eventStart);
    });
  }

  private toCentralISOString(date: Date): string {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const get = (type: string) => parts.find(p => p.type === type)!.value;

    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  }

  closeAppointmentDialog(): void {
    this.showAppointmentDialog = false;
    this.selectedSlotStart = null;
    this.selectedSlotEnd = null;
    this.appointmentSubject = '';
    this.appointmentNotes = '';
  }

  bookAppointment(): void {
    if (!this.selectedSlotStart || !this.selectedSlotEnd) {
      this.errorMessage = 'Please provide all required information.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const location = this.isInPersonMeeting() ? this.getLocationForDay(this.selectedSlotStart) : null;

    const request: CreateAppointmentRequest = {
      caseId: this.caseId,
      startDateTime: this.toCentralISOString(this.selectedSlotStart),
      endDateTime: this.toCentralISOString(this.selectedSlotEnd),
      location: location
    };

    this.calendarApiService.createAppointment(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.successMessage = 'Appointment booked successfully! Both users will receive a confirmation email.';
        this.closeAppointmentDialog();
        this.loadCalendarEvents();

        setTimeout(() => this.successMessage = '', 5000);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error booking appointment:', error);
        this.errorMessage = 'An error occurred while booking the appointment. Please try again.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  getDurationText(): string {
    const hours = Math.floor(this.meetingMinutes / 60);
    const minutes = this.meetingMinutes % 60;

    if (hours === 0) {
      return `${minutes} minutes`;
    } else if (minutes === 0) {
      return hours === 1 ? '1 hour' : `${hours} hours`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min`;
    }
  }

  /**
   * Checks if there are any available time slots remaining for a given day.
   */
  private hasAvailableSlotsForDay(day: Date): boolean {
    return this.buildSlotsForDay(day).some(s => s.available);
  }

  /**
   * Checks if the view should auto-advance to next week on initial load.
   */
  private shouldAdvanceToNextWeek(): boolean {
    const today = new Date();
    const dayOfWeek = getDay(today);

    for (let d = dayOfWeek; d <= 5; d++) {
      if (d === 0 || d === 6) {
        continue;
      }

      const daysFromToday = d - dayOfWeek;
      const checkDate = addDays(today, daysFromToday);

      if (this.hasAvailableSlotsForDay(checkDate)) {
        return false;
      }
    }

    return true;
  }

  /** Whether the given day is today. */
  isToday(date: Date): boolean {
    return isSameDay(date, new Date());
  }

  /**
   * Gets the office location for a specific day of the week.
   */
  getLocationForDay(date: Date): string | null {
    if (!this.employeeLocation) {
      return null;
    }

    const dayOfWeek = date.getDay();
    switch (dayOfWeek) {
      case 1: return this.employeeLocation.monday || null;
      case 2: return this.employeeLocation.tuesday || null;
      case 3: return this.employeeLocation.wednesday || null;
      case 4: return this.employeeLocation.thursday || null;
      case 5: return this.employeeLocation.friday || null;
      default: return null; // Weekend
    }
  }
}
